import clsx from 'clsx';
import type { AccountStatus, CampaignStatus, ChatHealth, DialogTemplateStatus } from '@/types/domain';

type AnyStatus = CampaignStatus | AccountStatus | ChatHealth | DialogTemplateStatus;

const LABEL: Record<AnyStatus, string> = {
  // campaign
  draft: 'черновик', ready: 'подготовка', running: 'идёт', paused: 'пауза', completed: 'завершена',
  // account
  active: 'активный', cooldown: 'cooldown', banned: 'забанен', unauthorized: 'не авторизован', deactivated: 'деактивирован',
  // chat-health
  unknown: 'не оценён', pending: 'оценка', alive: 'живой', quiet: 'тихий', spam: 'спам', dead: 'мёртвый', inaccessible: 'нет доступа',
  // dialog template (overlap with above is fine — same labels)
  approved: 'одобрен', rejected: 'отклонён', used: 'использован',
};

const TONE: Partial<Record<AnyStatus, 'good' | 'warn' | 'bad' | 'primary' | 'neutral'>> = {
  running: 'primary', ready: 'primary', alive: 'good', active: 'good', approved: 'good',
  paused: 'warn', cooldown: 'warn', quiet: 'warn', spam: 'warn', pending: 'warn',
  banned: 'bad', unauthorized: 'bad', deactivated: 'bad', dead: 'bad', inaccessible: 'bad', rejected: 'bad',
  draft: 'neutral', completed: 'neutral', unknown: 'neutral', used: 'neutral',
};

export function StatusPill({ status, withDot = true }: { status: AnyStatus; withDot?: boolean }): JSX.Element {
  const tone = TONE[status] ?? 'neutral';
  const cls =
    tone === 'good'    ? 'pill pill-good' :
    tone === 'warn'    ? 'pill pill-warn' :
    tone === 'bad'     ? 'pill pill-bad'  :
    tone === 'primary' ? 'pill pill-primary' :
    'pill';
  const dotColor =
    tone === 'good'    ? 'text-good' :
    tone === 'warn'    ? 'text-warn' :
    tone === 'bad'     ? 'text-bad'  :
    tone === 'primary' ? 'text-accent-300' :
    'text-ink-500';
  const pulse = status === 'running' || status === 'pending';
  return (
    <span className={cls}>
      {withDot && (
        <span
          className={clsx('dot bg-current', dotColor, pulse && 'dot-pulse')}
        />
      )}
      <span className="uppercase tracking-wide">{LABEL[status]}</span>
    </span>
  );
}
